import { getHours } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { addMinutes, differenceInMinutes, getHours as getLocalHours, isBefore } from "date-fns";

import {
  MAXIMUM_SHIFT_DURATION_MINUTES,
  MINIMUM_SHIFT_DURATION_MINUTES,
} from "../../../utils/shiftPostingUtils";
import { TIME_SLOT_BOUNDARIES, type TimeValidationError } from "../types";

import { type TuFunction } from "@src/appV2/i18n/types";
import { fallbackTu } from "@src/appV2/i18n/fallbackTu";

const PAST_START_TIME_BUFFER_MINUTES = 2;

function formatHour(hour: number, minute = 0): string {
  const amPm = hour >= 12 ? "PM" : "AM";

  let displayHour = hour;
  if (hour === 0) {
    displayHour = 12;
  } else if (hour > 12) {
    displayHour = hour - 12;
  }

  return `${displayHour}:${String(minute).padStart(2, "0")} ${amPm}`;
}

interface ValidateShiftTimesOptions {
  startTime: Date;
  endTime: Date;
  timeSlot: TimeSlot;
  timezone?: string;
  /**
   * When true, validates that start time is at least 2 minutes in the future.
   * Used for invites which don't support extended bookability.
   */
  validatePastStartTime?: boolean;
  /**
   * When true, validates that the start time falls within the current timeslot's boundary.
   * Use when timeslot changes are not allowed (e.g. when posting a new shift).
   * When false (default), timeslot changes are permitted and shown as an info message instead.
   */
  validateTimeslotBoundary?: boolean;
  /**
   * The workplace's *static* pre-configured start and end times for this slot
   * (sourced from the workplace's slot defaults, NOT the shift's current saved
   * time). When provided alongside `validateTimeslotBoundary`, the effective
   * slot range is widened to also include this default range, so admins can
   * edit within (and including) the workplace's intended hours even if those
   * defaults span beyond the strict slot bounds (e.g. PM defaults configured as
   * 11pm-2am).
   *
   * These must NOT be bound to the shift's mutable current time: doing so lets
   * each incremental save advance the accepted range, walking an AM shift into
   * PM/NOC hours one save at a time (SYN-2147). Anchoring to the static slot
   * defaults keeps the accepted range fixed across saves.
   */
  slotDefaultStartTime?: Date;
  slotDefaultEndTime?: Date;
  /**
   * When true, the out-of-bounds start message names the default-time context so
   * the user understands why the accepted range is stricter than a single-shift
   * edit.
   */
  isSettingDefault?: boolean;
}

function getHourInZone(date: Date, timezone?: string): number {
  return timezone ? getHours(date, { timeZone: timezone }) : getLocalHours(date);
}

function isHourInRange(hour: number, startHour: number, endHour: number): boolean {
  if (startHour <= endHour) {
    return hour >= startHour && hour <= endHour;
  }
  // Wraparound range (e.g. 23-2 covers hours 23, 0, 1, 2).
  return hour >= startHour || hour <= endHour;
}

function isHourInStrictBounds(hour: number, timeSlot: TimeSlot): boolean {
  const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
  return boundaries.crossesMidnight
    ? hour >= boundaries.startMin || hour <= boundaries.startMax
    : hour >= boundaries.startMin && hour <= boundaries.startMax;
}

function isStartTimeValidForSlot(
  startTime: Date,
  timeSlot: TimeSlot,
  timezone?: string,
  slotDefaultStartTime?: Date,
  slotDefaultEndTime?: Date
): boolean {
  const hour = getHourInZone(startTime, timezone);

  if (isHourInStrictBounds(hour, timeSlot)) {
    return true;
  }

  // If the workplace has pre-configured defaults that span beyond the strict
  // slot bounds (e.g. PM 11pm-2am), accept any hour that falls within that
  // configured range too. This widens the effective slot bounds rather than
  // dropping validation entirely. The range is anchored to the static slot
  // defaults, so it does not move as the shift's time is edited and saved.
  if (isDefined(slotDefaultStartTime) && isDefined(slotDefaultEndTime)) {
    const slotDefaultStartHour = getHourInZone(slotDefaultStartTime, timezone);
    const slotDefaultEndHour = getHourInZone(slotDefaultEndTime, timezone);
    if (isHourInRange(hour, slotDefaultStartHour, slotDefaultEndHour)) {
      return true;
    }
  }

  return false;
}

/**
 * The contiguous span of start hours accepted for a slot, as a single
 * [startHour, endHour] range (wrapping past midnight when endHour < startHour).
 *
 * It starts from the strict slot bounds and walks outward across whatever
 * adjacent hours the workplace's widener (`slotDefault*`) also accepts, so the
 * error message can show one range (e.g. NOC "5:00 PM to 7:00 AM") instead of a
 * confusing strict + widener "or" pair (SYN-2673).
 */
function getAcceptedStartHourRange(
  timeSlot: TimeSlot,
  timezone: string | undefined,
  slotDefaultStartTime: Date | undefined,
  slotDefaultEndTime: Date | undefined
): { startHour: number; endHour: number } {
  const widenerStartHour = isDefined(slotDefaultStartTime)
    ? getHourInZone(slotDefaultStartTime, timezone)
    : undefined;
  const widenerEndHour = isDefined(slotDefaultEndTime)
    ? getHourInZone(slotDefaultEndTime, timezone)
    : undefined;

  const isAccepted = (hour: number): boolean =>
    isHourInStrictBounds(hour, timeSlot) ||
    (isDefined(widenerStartHour) &&
      isDefined(widenerEndHour) &&
      isHourInRange(hour, widenerStartHour, widenerEndHour));

  const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
  let startHour = boundaries.startMin;
  let endHour = boundaries.startMax;
  // Bounded by 24 so an always-accepted set can't loop forever.
  for (let step = 0; step < 24 && isAccepted((startHour + 23) % 24); step += 1) {
    startHour = (startHour + 23) % 24;
  }
  for (let step = 0; step < 24 && isAccepted((endHour + 1) % 24); step += 1) {
    endHour = (endHour + 1) % 24;
  }

  return { startHour, endHour };
}

function getShiftDurationMinutes(startTime: Date, endTime: Date): number {
  let duration = differenceInMinutes(endTime, startTime);

  if (duration < 0) {
    duration += 24 * 60;
  }

  return duration;
}

export function validateShiftTimes(
  {
    startTime,
    endTime,
    timeSlot,
    timezone,
    validatePastStartTime = false,
    validateTimeslotBoundary = false,
    slotDefaultStartTime,
    slotDefaultEndTime,
    isSettingDefault = false,
  }: ValidateShiftTimesOptions,
  tu: TuFunction = fallbackTu
): TimeValidationError[] {
  const errors: TimeValidationError[] = [];

  // For invites: validate that start time is at least 2 minutes in the future
  if (validatePastStartTime) {
    const now = getServerNow();
    const minStartTime = addMinutes(now, PAST_START_TIME_BUFFER_MINUTES);

    if (isBefore(startTime, minStartTime)) {
      errors.push({
        field: "start",
        message: tu("The start time must be at least 2 minutes after the current time"),
      });
    }
  }

  if (
    validateTimeslotBoundary &&
    !isStartTimeValidForSlot(
      startTime,
      timeSlot,
      timezone,
      slotDefaultStartTime,
      slotDefaultEndTime
    )
  ) {
    // Present the strict bounds and the workplace widener as one accepted range
    // (e.g. NOC "5:00 PM and 5:59 AM") rather than a confusing "or between" pair.
    // The upper bound shows :59 because the whole ending hour is still accepted.
    const { startHour, endHour } = getAcceptedStartHourRange(
      timeSlot,
      timezone,
      slotDefaultStartTime,
      slotDefaultEndTime
    );
    const range = `${formatHour(startHour)} and ${formatHour(endHour, 59)}`;
    const slotLabel = timeSlot.toUpperCase();

    // Name the slot (its hours drive the range) and the scope: the default rule
    // spans all shifts of the slot, a single edit is about this one shift — so the
    // stricter default range doesn't look arbitrary next to the looser one.
    errors.push({
      field: "start",
      message: isSettingDefault
        ? tu("The default start time for {{slotLabel}} shifts must be between {{range}}", {
            values: { slotLabel, range },
          })
        : tu("The start time for {{slotLabel}} shifts must be between {{range}}", {
            values: { slotLabel, range },
          }),
    });
  }

  const duration = getShiftDurationMinutes(startTime, endTime);

  if (duration < MINIMUM_SHIFT_DURATION_MINUTES) {
    errors.push({
      field: "duration",
      message: tu("Shift duration must be at least {{minutes}} minutes", {
        values: { minutes: MINIMUM_SHIFT_DURATION_MINUTES },
      }),
    });
  }

  if (duration > MAXIMUM_SHIFT_DURATION_MINUTES) {
    errors.push({
      field: "duration",
      message: tu("Shift duration can't be longer than {{hours}} hours", {
        values: { hours: MAXIMUM_SHIFT_DURATION_MINUTES / 60 },
      }),
    });
  }

  if (validateTimeslotBoundary && isBefore(endTime, startTime) && timeSlot !== "noc") {
    const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
    if (!boundaries.crossesMidnight) {
      errors.push({
        field: "end",
        message: tu("End time cannot be before start time for this shift type"),
      });
    }
  }

  return errors;
}

export function hasValidationErrors(errors: TimeValidationError[]): boolean {
  return errors.length > 0;
}

export function getErrorForField(
  errors: TimeValidationError[],
  field: TimeValidationError["field"]
): string | undefined {
  return errors.find((error) => error.field === field)?.message;
}
